import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Row } from '@tanstack/react-table';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from 'src/registry/ui/Button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/registry/ui/Tooltip';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { BILLING_STATUS } from 'src/features/servicePlanBillingPolicies/utils/types';
import { ReferenceRateValue } from 'src/features/referenceRateValues/utils/types';
import { referenceRateValuesMessages } from 'src/features/referenceRateValues/utils/messages';
import UpdateReferenceRateValueFormModal from 'src/features/referenceRateValues/components/forms/UpdateReferenceRateValueFormModal';
import DeleteReferenceRateValueFormModal from 'src/features/referenceRateValues/components/forms/DeleteReferenceRateValueFormModal';

type CellReferenceRateValueActionsProps = {
    row: Row<ReferenceRateValue>;
};

/**
 * A `current` or `past` value is immutable, and only the last value in the series may be deleted.
 * The series is contiguous and open-ended, so the last value is the one without an end date.
 */
export default function CellReferenceRateValueActions({ row }: CellReferenceRateValueActionsProps) {
    const { formatMessage } = useIntl();

    const isScheduled = row.original.status === BILLING_STATUS.SCHEDULED;
    const isLastValue = row.original.valid_to === null;

    const canEdit = isScheduled;
    const canDelete = isScheduled && isLastValue;

    return (
        <div className="flex w-full justify-end gap-1">
            <TooltipProvider>
                <Tooltip>
                    {canEdit ? (
                        <UpdateReferenceRateValueFormModal
                            semantics={{ entryName: formatMessage(referenceRateValuesMessages.value).toLowerCase() }}
                            selectedReferenceRateValue={row.original}
                        >
                            <TooltipTrigger asChild>
                                <Button size="icon" variant="link" className="cursor-pointer hover:bg-gray-100">
                                    <FontAwesomeIcon icon={ICON_MAP.EDIT} />
                                </Button>
                            </TooltipTrigger>
                        </UpdateReferenceRateValueFormModal>
                    ) : (
                        <TooltipTrigger asChild>
                            <span>
                                <Button size="icon" variant="link" disabled className="opacity-40">
                                    <FontAwesomeIcon icon={ICON_MAP.EDIT} />
                                </Button>
                            </span>
                        </TooltipTrigger>
                    )}
                    <TooltipContent>
                        {canEdit ? (
                            <FormattedMessage {...generalMessages.edit} />
                        ) : (
                            <FormattedMessage {...referenceRateValuesMessages.cannotEditNotScheduled} />
                        )}
                    </TooltipContent>
                </Tooltip>
                <Tooltip>
                    {canDelete ? (
                        <DeleteReferenceRateValueFormModal selectedReferenceRateValue={row.original}>
                            <TooltipTrigger asChild>
                                <Button size="icon" variant="link" className="cursor-pointer hover:bg-gray-100">
                                    <FontAwesomeIcon icon={ICON_MAP.DELETE} />
                                </Button>
                            </TooltipTrigger>
                        </DeleteReferenceRateValueFormModal>
                    ) : (
                        <TooltipTrigger asChild>
                            <span>
                                <Button size="icon" variant="link" disabled className="opacity-40">
                                    <FontAwesomeIcon icon={ICON_MAP.DELETE} />
                                </Button>
                            </span>
                        </TooltipTrigger>
                    )}
                    <TooltipContent>
                        {canDelete ? (
                            <FormattedMessage {...generalMessages.delete} />
                        ) : (
                            <FormattedMessage {...referenceRateValuesMessages.cannotDeleteNotScheduled} />
                        )}
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        </div>
    );
}
